import React, { useState } from 'react';
import {
  VStack, SimpleGrid, FormControl, FormLabel, Input, Select, Textarea,
  Button, HStack, useToast,
} from '@chakra-ui/react';
import {
  ImmAgenda, crearAgenda, actualizarAgenda,
} from '../../../api/immApi';
import {
  SectionTitle, Field, IMM_COLOR, IMM_COLOR_HOVER, fmt,
} from '../_shared';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: ImmAgenda;
  onGuardado?: (a: ImmAgenda) => void;
  onCancelar?: () => void;
}

const TIPOS = ['Reunion', 'Oficio', 'Invitacion', 'Conferencia', 'Evento', 'Cita'];
const ESTATUS_AGENDA = ['Programada', 'Realizada', 'Reagendada', 'Cancelada'];
const hoy = () => new Date().toISOString().split('T')[0];

const FormularioAgenda: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const toast = useToast();
  const [guardando, setGuardando] = useState(false);

  const [f, setF] = useState<Partial<ImmAgenda>>({
    fecha: data?.fecha ? data.fecha.split('T')[0] : hoy(),
    hora: data?.hora || '',
    lugar: data?.lugar || '',
    tema: data?.tema || '',
    tipo: data?.tipo || '',
    observaciones: data?.observaciones || '',
    estatus: data?.estatus || 'Programada',
  });

  const set = (campo: keyof ImmAgenda, valor: any) => setF((prev) => ({ ...prev, [campo]: valor }));

  const handleGuardar = async () => {
    if (!f.fecha) {
      toast({ title: 'La fecha es obligatoria', status: 'warning', duration: 3000 });
      return;
    }
    if (!f.tipo) {
      toast({ title: 'El tipo es obligatorio', status: 'warning', duration: 3000 });
      return;
    }
    setGuardando(true);
    try {
      const payload: Partial<ImmAgenda> = { ...f };
      const res = modo === 'edit' && data
        ? await actualizarAgenda(data.id, payload)
        : await crearAgenda(payload);
      toast({ title: modo === 'edit' ? 'Registro actualizado' : 'Registro creado', status: 'success', duration: 3000 });
      onGuardado?.(res);
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo guardar el registro.', status: 'error', duration: 4000 });
    } finally {
      setGuardando(false);
    }
  };

  if (modo === 'view' && data) {
    return (
      <VStack spacing={3} align="stretch">
        <SectionTitle>Datos del registro</SectionTitle>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          <Field label="Fecha" value={fmt(data.fecha)} />
          <Field label="Hora" value={data.hora} />
          <Field label="Tipo" value={data.tipo} />
          <Field label="Tema" value={data.tema} />
          <Field label="Lugar" value={data.lugar} />
          <Field label="Estatus" value={data.estatus} />
        </SimpleGrid>
        <SectionTitle>Observaciones</SectionTitle>
        <SimpleGrid columns={{ base: 1 }} spacing={4}>
          <Field label="Observaciones" value={data.observaciones} />
        </SimpleGrid>
        <HStack justify="flex-end" pt={2}>
          <Button variant="outline" onClick={onCancelar}>Cerrar</Button>
        </HStack>
      </VStack>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      <SectionTitle>Datos del registro</SectionTitle>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Fecha</FormLabel>
          <Input type="date" value={(f.fecha as string) || ''} onChange={(e) => set('fecha', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Hora</FormLabel>
          <Input type="time" value={f.hora || ''} onChange={(e) => set('hora', e.target.value)} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel fontSize="sm">Tipo</FormLabel>
          <Select placeholder="Selecciona" value={f.tipo || ''} onChange={(e) => set('tipo', e.target.value)}>
            {TIPOS.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </FormControl>
        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Tema</FormLabel>
          <Input value={f.tema || ''} onChange={(e) => set('tema', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Lugar</FormLabel>
          <Input value={f.lugar || ''} onChange={(e) => set('lugar', e.target.value)} />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Estatus</FormLabel>
          <Select value={f.estatus || 'Programada'} onChange={(e) => set('estatus', e.target.value)}>
            {ESTATUS_AGENDA.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </FormControl>
      </SimpleGrid>

      <SectionTitle>Observaciones</SectionTitle>
      <SimpleGrid columns={{ base: 1 }} spacing={4}>
        <FormControl>
          <FormLabel fontSize="sm">Observaciones</FormLabel>
          <Textarea rows={3} value={f.observaciones || ''} onChange={(e) => set('observaciones', e.target.value)} />
        </FormControl>
      </SimpleGrid>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>Cancelar</Button>
        <Button bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={handleGuardar} isLoading={guardando} loadingText="Guardando...">
          Guardar registro
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioAgenda;
